<script setup lang="ts">
import { app as appInfo } from '@shared/lib/app'
import { computed } from 'vue'
import { isAnswered } from '../lib/answers'
import { formatDateTime } from '../lib/time'
import { formatScore } from '../lib/score'
import type { AttemptView, Participant } from '../types'

const props = defineProps<{
  initial: AttemptView
  participant: Participant
}>()

const emit = defineEmits<{ logout: []; schedule: [] }>()

const score = computed(() => props.initial.score ?? null)
const showScore = computed(() => Boolean(props.initial.exam.show_result && score.value))
const answeredCount = computed(
  () => props.initial.responses.filter((response) => isAnswered(response.response)).length,
)
const percentage = computed(() =>
  score.value && score.value.max_total > 0
    ? Math.round((score.value.auto_total / score.value.max_total) * 100)
    : 0,
)
const correctCount = computed(
  () => score.value?.decisions.filter((decision) => decision.rationale === 'correct').length ?? 0,
)
const itemOrder = computed(() => {
  const map = new Map<string, number>()
  for (const item of props.initial.items) map.set(item.id, item.order)
  return map
})
const stemById = computed(() => {
  const map = new Map<string, string>()
  for (const item of props.initial.items) map.set(item.id, item.presentation.stem)
  return map
})

function rationaleLabel(rationale: string): string {
  switch (rationale) {
    case 'correct':
      return 'Benar'
    case 'incorrect':
      return 'Salah'
    case 'unanswered':
      return 'Tidak dijawab'
    case 'partial':
      return 'Sebagian benar'
    case 'pending_manual':
      return 'Menunggu penilaian guru'
    case 'accepted_all':
    case 'correct_alt_key':
      return 'Benar'
    case 'voided':
      return 'Dibatalkan'
    default:
      return 'Tidak valid'
  }
}

function printReceipt(): void {
  window.print()
}
</script>

<template>
  <div class="result-shell">
    <header class="result-header">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">OAP</span>
        <span class="brand-text">
          <strong>{{ appInfo.name }}</strong>
          <small>{{ initial.exam.id }} · v{{ initial.exam.version }}</small>
        </span>
      </div>
      <span class="status-chip status-chip--success">
        <span class="status-dot status-dot--saved" aria-hidden="true" />
        Jawaban terkumpul
      </span>
    </header>

    <main id="main-content" class="result-main">
      <section class="result-hero" aria-labelledby="result-heading">
        <div class="result-hero__icon" aria-hidden="true">✓</div>
        <p class="eyebrow">Selesai</p>
        <h1 id="result-heading">Jawaban berhasil dikumpulkan</h1>
        <p class="lede">
          {{ participant.display_name }}, jawaban Anda untuk <strong>{{ initial.exam.title }}</strong>
          sudah diterima server pada {{ formatDateTime(initial.attempt.submitted_at) }}.
        </p>
        <div class="result-actions">
          <button class="button button--primary" type="button" @click="printReceipt">
            Cetak bukti
          </button>
          <button class="button button--secondary" type="button" @click="emit('schedule')">
            Lihat jadwal ujian
          </button>
          <button class="button button--secondary" type="button" @click="emit('logout')">
            Keluar sesi
          </button>
        </div>
      </section>

      <section v-if="showScore && score" class="score-card" aria-labelledby="score-heading">
        <div class="score-card__head">
          <div>
            <p class="eyebrow">Hasil otomatis</p>
            <h2 id="score-heading">Skor sementara</h2>
          </div>
          <div class="score-number" :aria-label="`Skor ${formatScore(score.auto_total, score.unit)} dari ${formatScore(score.max_total, score.unit)}`">
            <strong>{{ formatScore(score.auto_total, score.unit) }}</strong>
            <span>/ {{ formatScore(score.max_total, score.unit) }}</span>
          </div>
        </div>

        <div class="score-bar" role="progressbar" :aria-valuenow="percentage" aria-valuemin="0" aria-valuemax="100">
          <span :style="{ width: `${percentage}%` }" />
        </div>
        <p class="score-caption">{{ percentage }}% · {{ correctCount }} soal benar</p>

        <details class="breakdown">
          <summary>Lihat rincian per soal ({{ score.decisions.length }})</summary>
          <table>
            <thead>
              <tr>
                <th scope="col">Soal</th>
                <th scope="col">Status</th>
                <th scope="col">Poin</th>
                <th scope="col">Ringkas</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="decision in score.decisions"
                :key="decision.item_id"
                :class="`decision decision--${decision.rationale}`"
              >
                <td>{{ itemOrder.get(decision.item_id) ?? '—' }}</td>
                <td>
                  <span class="decision-badge">{{ rationaleLabel(decision.rationale) }}</span>
                </td>
                <td>{{ decision.awarded }} / {{ decision.max }}</td>
                <td class="decision-stem">{{ stemById.get(decision.item_id) ?? decision.item_id }}</td>
              </tr>
            </tbody>
          </table>
        </details>

        <p class="side-note">
          Skor ini dihitung otomatis dari kunci jawaban pada paket ujian. Penilaian akhir tetap
          mengikuti kebijakan penyelenggara.
        </p>
      </section>

      <section v-else class="result-note" aria-label="Catatan hasil">
        <h2>Hasil tidak ditampilkan</h2>
        <p>
          Paket ujian ini tidak menampilkan skor kepada peserta. Hubungi pengawas atau
          penyelenggara untuk informasi hasil.
        </p>
      </section>

      <section class="receipt-card" aria-labelledby="receipt-heading">
        <h2 id="receipt-heading">Ringkasan sesi</h2>
        <dl class="receipt-grid">
          <div>
            <dt>Peserta</dt>
            <dd>{{ participant.display_name }}</dd>
          </div>
          <div>
            <dt>ID peserta</dt>
            <dd><code>{{ participant.id }}</code></dd>
          </div>
          <div>
            <dt>Ujian</dt>
            <dd>{{ initial.exam.title }}</dd>
          </div>
          <div>
            <dt>Attempt</dt>
            <dd><code>{{ initial.attempt.id }}</code></dd>
          </div>
          <div>
            <dt>Dikumpulkan</dt>
            <dd>{{ formatDateTime(initial.attempt.submitted_at) }}</dd>
          </div>
          <div>
            <dt>Terjawab</dt>
            <dd>{{ answeredCount }} dari {{ initial.exam.item_count }} soal</dd>
          </div>
        </dl>
        <p class="side-note">
          Simpan tangkapan layar halaman ini sebagai bukti sementara. Bukti resmi tetap berasal
          dari paket hasil yang diimpor penyelenggara.
        </p>
      </section>
    </main>

    <footer class="result-footer">
      <span>Open Assessment Platform · eksperimen runtime CBT</span>
      <span>Halaman ini bukan dokumen resmi</span>
    </footer>
  </div>
</template>
